import { useEffect, useState, useRef, useCallback } from "react";
import { Database, RefreshCw, CalendarClock } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

// FileMaker -> App one-way product sync (admin only)
export function FileMakerSyncCard() {
  const { t } = useLanguage();
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [fixing, setFixing] = useState(false);

  const backfillCreatedAt = async () => {
    setFixing(true);
    try {
      const { data } = await api.post("/products/backfill-created-at");
      toast.success(t("fm.fixCreatedAtDone").replace("{n}", String(data.updated ?? 0)));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setFixing(false);
    }
  };
  const timer = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/filemaker/sync/status");
      setStatus(data);
      if (data.state !== "running" && timer.current) {
        clearInterval(timer.current);
        timer.current = null;
      }
    } catch (error) {
      // not admin or backend down — hide silently but log for devs
      if (error?.response?.status !== 403 && error?.response?.status !== 401) {
        console.warn("FileMaker sync status fetch failed:", error);
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [fetchStatus]);

  useEffect(() => {
    if (status?.state === "running" && !timer.current) {
      timer.current = setInterval(fetchStatus, 3000);
    }
  }, [status, fetchStatus]);

  const startSync = async () => {
    setStarting(true);
    try {
      await api.post("/filemaker/sync");
      toast.success(t("fm.started"));
      fetchStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setStarting(false);
    }
  };

  const running = status?.state === "running";
  const pct = status?.total ? Math.round((status.processed / status.total) * 100) : 0;

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-4 mb-5" data-testid="filemaker-sync-card">
      <h3 className="font-heading text-base font-bold tracking-tight flex items-center gap-2">
        <Database size={16} className="text-brand" /> {t("fm.title")}
      </h3>
      <p className="text-xs text-muted-foreground -mt-2">{t("fm.hint")}</p>

      {status && status.state !== "idle" && (
        <div className="space-y-2" data-testid="fm-sync-status">
          {running && (
            <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-brand transition-all duration-500" style={{ width: `${pct}%` }} />
            </div>
          )}
          <p className="text-xs font-mono text-muted-foreground">
            {running ? `${t("fm.syncing")} ${status.processed}/${status.total} (${pct}%)` :
              status.state === "done" ? t("fm.done") : `${t("fm.failed")}: ${status.error || ""}`}
            {" · "}{t("fm.created")} {status.created} · {t("fm.updated")} {status.updated} · {t("fm.photos")} {status.photos} · {t("fm.thumbs")} {status.thumbs ?? 0} · {t("fm.priceRows")} {status.price_rows ?? 0}
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <Button onClick={startSync} disabled={running || starting} data-testid="fm-sync-button"
          variant="outline" className="rounded-md">
          <RefreshCw size={14} className={running ? "animate-spin" : ""} />
          {running ? t("fm.syncing") : t("fm.syncNow")}
        </Button>
        {/* 2026-06 · One-off: rewrite created_at with the real FM 建档日期. */}
        <Button onClick={backfillCreatedAt} disabled={fixing} data-testid="fm-backfill-created-at"
          variant="outline" className="rounded-md">
          <CalendarClock size={14} /> {fixing ? "…" : t("fm.fixCreatedAt")}
        </Button>
      </div>
    </div>
  );
}
